import TextField from '@mui/material/TextField';

import { useDraft } from './draft';

/**
 * A text field that saves what changed when it loses focus — spec 013 §12.
 *
 * The one component this screen adds to MUI, and it earns that by being the place the
 * save rule lives: five field kinds otherwise repeat the same draft-and-compare, and the
 * fifth one gets it subtly wrong.
 *
 * Saving on blur rather than behind a button is the point of "first-class, not a
 * fallback" — a console where fixing a word costs a click on Save and a click on
 * Regenerate teaches people to use Regenerate.
 */
export function EditableText({
  id,
  label,
  value,
  multiline = false,
  disabled = false,
  onSave,
}: {
  /** Where a publish refusal about this field jumps to. */
  id?: string;
  label: string;
  value: string;
  multiline?: boolean;
  disabled?: boolean;
  onSave: (next: string) => void;
}) {
  const { draft, setDraft, reset } = useDraft(value);

  return (
    <TextField
      id={id}
      label={label}
      value={draft}
      size="small"
      fullWidth
      multiline={multiline}
      disabled={disabled}
      onChange={(event) => {
        setDraft(event.target.value);
      }}
      onBlur={() => {
        const next = draft.trim();

        // Empty is a slip, not an edit: every one of these columns is non-null, so the
        // write would be refused by the API and the reviewer would lose their text.
        if (next === '' || next === value) {
          reset();
          return;
        }

        onSave(next);
      }}
    />
  );
}
